/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * This file contains the component that provides context for the online patient
 * management system.
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

/*
 * Used to get access to router context.
 */
import { useNavigate, useParams } from 'react-router-dom';

/*
 * Used to style components
 */
import { AlertTitle, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid2, Paper, styled, TextField, Typography } from '@mui/material';

/**
 * Used for the basic page layout.
 */
import {
    ALL_INSTITUTIONS_CODE,
    CollapsibleTable,
    Form,
    FormSubmitButton,
    GroupedField,
    IFormValidationError,
    InstitutionBreadcrumbs,
    InstitutionsContext,
    OnlinePatientManagementContext,
    PageLayout,
    PatientsByCodesProvider,
    PatientsContext,
    RouteLoading,
    Select,
    TrialContextSelector,
    useFormByCodes,
    useFormLookups,
    useFormMetadata,
    useFormsByIds,
    useLookups,
    useSnackbar,
    ValidationErrorType
} from '@ngt/opms';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */

/*
 * Used to type patient state.
 */
import { GridColDef, GridEventListener } from '@mui/x-data-grid';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { ChangeEventHandler, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
// import * as Dtos from '../api/dtos';
import { allStates, AppSettings } from '@/AppSettings';
import ScreeningForm from '@/components/forms/ScreeningForm';
import FinanceMilestonesImportButton from '@/components/FinanceMilestonesImportButton';
import ConsentDialogContext, { ConsentDialogState } from '@/contexts/ConsentDialogContext';
import ScreeningDialogContext from '@/contexts/ScreeningDialogContext';
import useConsentTableColumns from '@/hooks/columns/useConsentTableColumns';
import useScreenFailTableColumns from '@/hooks/columns/useScreenFailTableColumns';
import useScreeningTableColumns from '@/hooks/columns/useScreeningTableColumns';
import { IFormSubmit, IFormValidate, ValidateOn } from '@ngt/forms-core';
import { useSWRConfig } from 'swr';
import { FormDefinitionType, Institution, LookupScreeningOutcomeType, Patient, PatientStateType, QueueConsentInformationEmail, ScreeningForm as ScreeningFormDto, ScreeningFormPostSave } from '../api/dtos';
import useRandomisedTableColumns from '@/hooks/columns/useRandomisedTableColumns';

/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface IRegistrationProps {

}

interface IPatientTableProps {
    title: string;
    entityName: string;
    columns: Array<GridColDef<Patient>>;
    onRowClick?: GridEventListener<'rowClick'>;
    patientStates?: Array<number>;
    startCollapsed?: boolean;
    patientsToUse?: Array<Patient>;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

//const MemoizedCollapsibleTable = memo(CollapsibleTable);


const SearchBox = styled(Box)(() => ({
    display: 'flex',
    gap: '1rem',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'end'
}));

const SearchTextField = styled(TextField)(({ theme }) => ({
    width: '62%',

    [theme.breakpoints.down('md')]: {
        width: '100%'
    }
}));

const SearchButtonBox = styled(Box)(({ theme }) => ({
    width: '35%',

    [theme.breakpoints.down('md')]: {
        width: '100%'
    }
}));

const PaperContainer = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3)
}));

const AddButton = styled(Button)(({ theme }) => ({
    width: '35%',

    [theme.breakpoints.down('md')]: {
        width: '100%'
    }
}));

const StyledButtonGroup = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 2, 2, 2),
    textAlign: 'center',
    gap: theme.spacing(2),
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    padding: theme.spacing(2),

    '&>p': {
        padding: theme.spacing(0),
        fontSize: '1.25rem',
        fontWeight: 500
    }
}));

const StyledDialogContent = styled(DialogContent)(({theme}) => ({
    padding: theme.spacing(0, 0),
    overflow: 'visible',

    '&>p': {
        padding: `${theme.spacing(0, 2, 2, 2)} !important`,
    }
}));

const pageSizeOptions = [5, 10, 20, 50, 100];

const screeningStates = [
    PatientStateType.Screening
]

const consentStates = [
    PatientStateType.Consent
];

const screeningFailedStates = [
    PatientStateType.ScreeningFailed
]

const randomisedStates = [
    PatientStateType.OnStudy
]

const PatientTable: FC<IPatientTableProps> = ({
    title,
    entityName,
    columns,
    onRowClick,
    patientStates,
    startCollapsed,
    patientsToUse: patients
}) => {
    const [tableCount, setTableCount] = useState(1);

    const { data: contextPatients, loading: contextPatientsLoading } = useContext(PatientsContext);

    const patientsLoadingToUse = contextPatientsLoading;

    const patientsToUse = useMemo(() => {
        if (patients != null) {
            return patients;
        }

        return contextPatients?.filter(patient => (patientStates ?? []).length === 0 || patientStates?.includes(patient.patientStateId!))

    }, [contextPatients, patientStates, patients])

    useEffect(() => {
        setTableCount(x => x + 1);
    }, [contextPatients, columns, patients]);

    return (
        <>
            <CollapsibleTable
                key={tableCount}
                rows={patientsToUse ?? []}
                loading={patientsLoadingToUse}
                columns={columns as any}
                onRowClick={onRowClick as any}
                //options={tableOptions}
                pageSizeOptions={pageSizeOptions}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                title={title ?? "Participants"}
                pluralizeTitle={false}
                entityName={entityName ?? "Participant"}
                disabledTableText={"There are no participants"}
                startCollapsed={startCollapsed}
            />
        </>

    );
}

const Search: FC = () => {
    const navigate = useNavigate();

    const { data: patients } = useContext(PatientsContext);

    const [searchValue, setSearchValue] = useState<string | null>(null);

    const { enqueueSnackbar } = useSnackbar();

    const saveText: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = useCallback((e) => {
        if (!e.target.value || e.target.value === '') {
            setSearchValue(null);
        }
        else {
            setSearchValue(e.target.value);
        }
    }, [searchValue, setSearchValue]);

    const search = useCallback(() => {
        if (patients?.some(p => p.studyNumber === searchValue)) {
            navigate(`/registration/all-institutions/` + searchValue);
        }
        else {
            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Participant Not Found
                    </AlertTitle>
                    {searchValue ? <>Participant '{searchValue}' not found. Please ensure you have correctly input the participant's study number.</> : <>The search field is empty.</>}
                </>,
                { variant: 'error' }
            );
        }
    }, [searchValue, navigate]);

    return (
        <SearchBox>
            <SearchTextField
                variant="standard"
                label=" Participant Study Number"
                id='patientNumberSearch'
                onChange={saveText}
            />
            <SearchButtonBox>
                <Button
                    variant='contained'
                    onClick={search}
                    fullWidth
                >
                    Search
                </Button>
            </SearchButtonBox>
        </SearchBox>
    )
}

const ConsentDialog: FC = () => {
    const { serviceStackClient: client } = useContext(OnlinePatientManagementContext);
    const { enqueueSnackbar } = useSnackbar();

    const { dialogState, setDialogState, patientStudyNumber, setPatientStudyNumber } = useContext(ConsentDialogContext);
    const { data: patients, refresh: patientRefresh } = useContext(PatientsContext);
    const { data: institutions} = useContext(InstitutionsContext);

    const patient = useMemo(() => {
        return patients?.find(x => x.studyNumber === patientStudyNumber)
    }, [patients, patientStudyNumber]);
    
    const institution = useMemo(() => {
        return institutions?.find(x => x.id === patient?.institutionId)
    }, [institutions, patient]);

    const formMetadata = useFormMetadata('screening');

    const {
        data: screeningFormlookups
    } = useLookups(formMetadata);

    const formScreeningFormlookups = useFormLookups(screeningFormlookups);

    const { data: screeningForm, loading: screeningFormLoading } = useFormByCodes(formMetadata, institution?.code, patient?.studyNumber, 'screening', 1, 1, false, false, false);

    const closeDialog = useCallback(() => {
        setDialogState(ConsentDialogState.None);
        setPatientStudyNumber(null);
    }, [setDialogState, setPatientStudyNumber]);


    const consentRefuseValidate: IFormValidate<ScreeningFormDto, IFormValidationError> = useCallback(async (formState) => {

        let errors: Record<string, IFormValidationError[]> = {};

        if (formState.value?.screenFailReason === undefined) {
            errors['screenFailReason'] = [
                {
                    property: 'screenFailReason',
                    code: 'SF-001',
                    message: 'This field is required.',
                    detailedMessage: 'This field is required.',
                    type: ValidationErrorType.Normal
                }
            ];
        }

        return errors;
    }, [institution]);

    const [consentSaving, setConsentSaving] = useState(false); 
    const [, setSendingEmail] = useState(false);

    const onNotifyClick = useCallback(async () => {
        setSendingEmail(true);

        try {
            await client.post(new QueueConsentInformationEmail({
                patientId: patient?.id
            }));

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Notification Sent
                    </AlertTitle>
                    The notification was successfully sent.
                </>,
                { variant: 'success' }
            );

            closeDialog();
            setSendingEmail(false);
        }
        catch {
            enqueueSnackbar(
                <>
                    <AlertTitle>
                    Notification Not Sent
                    </AlertTitle>
                    An error occurred while attempting to send the notification.
                </>,
                { variant: 'critical' }
            );

            closeDialog();
            setSendingEmail(false);
        }
    }, [patient, enqueueSnackbar, client, setSendingEmail, closeDialog]);

    const { cache } = useSWRConfig();

    const onRefuseSubmit: IFormSubmit<ScreeningFormDto, IFormValidationError> = useCallback(async (form) => {
        
        setConsentSaving(true);

        try {
            await client.post(new ScreeningFormPostSave({
                form: new ScreeningFormDto({ 
                    ...form.value, 
                    outcome: LookupScreeningOutcomeType.Fail 
                })
            }));

            closeDialog();

            patientRefresh();

            for (let key of Array.from(cache.keys()).filter(x => x.startsWith('SwrClient:ScreeningFormGetSingleByIds'))) {
                cache.delete(key);
            }

            setConsentSaving(false);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Partipant Marked As Screen Fail
                    </AlertTitle>
                    Participant was marked as screen fail successfully.
                </>,
                { variant: 'success' }
            );
        }
        catch (error: any) {
            setConsentSaving(false);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Partipant Not Marked As Screen Fail
                    </AlertTitle>
                    {error?.responseStatus?.errorCode === 'ClientViewableException' ? error?.responseStatus.message : 'An error occurred while attempting to mark the participant as screen fail.'}
                </>,
                { variant: 'critical' }
            );
        }    
    }, [client, enqueueSnackbar, closeDialog, setConsentSaving, patientRefresh, patient, cache]);

    return <>
        <Dialog
            open={dialogState === ConsentDialogState.ResendPip}
            onClose={closeDialog}
            aria-labelledby="notify-dialog-title"
            aria-describedby="notify-dialog-description"
        >
            <StyledDialogTitle id="notify-dialog-title">
                <Typography
                    color="secondary"
                >
                    Resend PIP (Participant {patientStudyNumber})
                </Typography>
            </StyledDialogTitle>
            <StyledDialogContent>
                <DialogContentText id="create-dialog-description">
                    This will send an email notification to the participant.
                </DialogContentText>
                <DialogContentText id="create-dialog-description">
                    Are you sure you wish to continue?
                </DialogContentText>
            </StyledDialogContent>
            <DialogActions style={{paddingLeft: 0, paddingRight: 0}}>
                <StyledButtonGroup
                    display="flex"
                    justifyContent="flex-end"
                >
                    <Button onClick={closeDialog} color="secondary">
                        No
                    </Button>
                    <Button onClick={onNotifyClick} color="primary" variant="contained" autoFocus>
                        Yes
                    </Button>
                </StyledButtonGroup>
            </DialogActions>
        </Dialog>
        <Dialog
            open={dialogState === ConsentDialogState.Refuse}
            onClose={closeDialog}
            aria-labelledby="refuse-dialog-title"
            aria-describedby="refuse-dialog-description"
            maxWidth="sm"
            fullWidth
        >
            <StyledDialogTitle id="refuse-dialog-title">
                <Typography
                    color="secondary"
                >
                    Refuse Consent (Participant {patientStudyNumber})
                </Typography>
            </StyledDialogTitle>
            <StyledDialogContent>
                <DialogContentText id="create-dialog-description">
                    The participant will be marked as screen fail.
                </DialogContentText>

                <Form
                    initialValue={screeningForm}
                    onSubmit={onRefuseSubmit}   
                    lookups={formScreeningFormlookups}
                    validateOn={ValidateOn.onChange} 
                    onValidate={consentRefuseValidate}
                    debug={AppSettings.debug}
                >
                    <GroupedField
                        propertyPath="screenFailReason"
                        LabelProps={{
                            label: "Please provide the details below" as any
                        }}
                        component={Select}
                        FieldGridProps={{
                            size: {
                                xs: 12,
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 12
                            }
                        }}
                    />

                    <StyledButtonGroup
                        display="flex"
                        justifyContent="flex-end"
                    >
                        <Button
                            color="secondary"
                            onClick={closeDialog}
                            loading={screeningFormLoading || consentSaving}  
                        >
                            Cancel
                        </Button>

                        <FormSubmitButton
                            color="primary"   
                            variant="contained"     
                            loading={screeningFormLoading || consentSaving}                                    
                        >
                            Mark as Screen Fail
                        </FormSubmitButton>
                    </StyledButtonGroup>
                </Form>
            </StyledDialogContent>
        </Dialog>
    </>
}


interface IScreeningTableProps {
}

const ScreeningTable: FC<IScreeningTableProps> = () => {
    const { institutionCode } = useParams();

    const { data: institutions } = useContext(InstitutionsContext);

    const { setPatientStudyNumber, setDialogOpen, setScreeningOutcome } = useContext(ScreeningDialogContext);

    const parsedInstitutionCode = useMemo(() => {
        return institutionCode !== ALL_INSTITUTIONS_CODE ? institutionCode : null;
    }, [institutionCode]);

    const onScreeningRowClick: GridEventListener<'rowClick'> = useCallback((params) => {
        setPatientStudyNumber(params.row?.studyNumber ?? null);
        setScreeningOutcome(null);
        setDialogOpen(true);
    }, [setPatientStudyNumber, setDialogOpen, setScreeningOutcome]);

    const institution = useMemo(() => {
        return institutions?.find(institution => institution.code === parsedInstitutionCode);
    }, [parsedInstitutionCode, institutions]);
    
    const {
        permissions: [canAdministerOpms],
    } = usePermissionsByIds(
        [
            Permission.OpmsDataManage, // BCT staff + CRA/CSM
        ], 
        undefined, 
        undefined, 
        institution?.id
    );

    const screeningColumns = useScreeningTableColumns(institutions ?? null, !canAdministerOpms);

    return <>
        <PatientTable
            title="Screening"
            entityName="Participant"
            onRowClick={onScreeningRowClick}
            columns={screeningColumns}
            patientStates={screeningStates}
        />
    </>
}



const ConsentTable: FC<IScreeningTableProps> = () => {
    const navigate = useNavigate();

    const { institutionCode } = useParams();

    const parsedInstitutionCode = useMemo(() => {
        return institutionCode !== ALL_INSTITUTIONS_CODE ? institutionCode : null;
    }, [institutionCode]);

    const { data: institutions } = useContext(InstitutionsContext);

    const showInstitutions = useMemo(() => {
        return !parsedInstitutionCode
    }, [parsedInstitutionCode]);

    const onRowClick: GridEventListener<'rowClick'> = useCallback((params) => {
        navigate(`/registration/${institutionCode ?? ALL_INSTITUTIONS_CODE}/${params.row?.studyNumber}`)
    }, [history, institutionCode]);

    const institution = useMemo(() => {
        return institutions?.find(institution => institution.code === parsedInstitutionCode);
    }, [parsedInstitutionCode, institutions]);

    const {
        permissions: [/*canOpmsAdminister, */canConsentAdminister],
    } = usePermissionsByIds(
        [
            // Permission.OpmsDataManage,
            Permission.EConsentAdminister,
        ], 
        undefined, 
        undefined, 
        institution?.id
    );

    const consentColumns = useConsentTableColumns(institutions, showInstitutions, canConsentAdminister);

    return (
        <PatientTable
            title="Consent"
            entityName="Participant"
            onRowClick={onRowClick}
            columns={consentColumns}
            patientStates={consentStates}
        />
    );
}

const Registration: FC<IRegistrationProps> = ({
}) => {
    const navigate = useNavigate();

    const { masterGroupCode, collaboratingGroupCode, countryCode, institutionCode } = useParams();

    const parsedInstitutionCode = useMemo(() => {
        return institutionCode !== ALL_INSTITUTIONS_CODE ? institutionCode : undefined;
    }, [institutionCode]);

    const { data: institutions } = useContext(InstitutionsContext);

    const screeningFormMetadata = useFormMetadata(FormDefinitionType.ScreeningForm);
    const {
        data: screeningForms,
        loading: screeningFormsLoading,
        refresh: refreshScreeningForms
    } = useFormsByIds<ScreeningFormDto>(screeningFormMetadata);

    const institution = useMemo(() => {
        return institutions?.find(institution => institution.code === parsedInstitutionCode);
    }, [parsedInstitutionCode, institutions]);

    const institutionsToUse = useMemo(() => {
        return institutions?.filter(i => i.code);
    }, [institutions]);

    const {
        permissions: [canAdministerOpms, canPatientUpdate, canAdministerRadiology, canCrfUpdate],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsDataManage, // BCT staff + CRA/CSM
            Permission.OpmsPatientUpdate,
            Permission.RadiologyReviewAccess,
            Permission.EcrfView,
            Permission.EConsentAdminister
        ], 
        undefined, 
        undefined, 
        institution?.id
    );

    const isRadiologist = canAdministerRadiology && !canCrfUpdate;

    // const { data: user } = useContext(AuthenticatedUserContext);

    // const { data: treatments } = useContext(TreatmentsContext);


    // const [saPatient, setSaPatient] = useState<Patient | undefined>(undefined);

    // const handleSaConsentClick = useCallback((patient: Patient) => {
    //     setSaPatient(patient)
    // }, [setSaPatient]);

    // const handleCancelClick = useCallback(() => {
    //     setSaPatient(undefined);
    // }, [setSaPatient]);

    // const handleReviewClick = useCallback(() => {
    //     setSaPatient(undefined);
    // }, [setSaPatient]);

    const failedScreeningColumns = useScreenFailTableColumns(
        institutions ?? null,
        screeningForms,
        screeningFormsLoading
    );
    // const screeningColumns = useScreeningTableColumns(institutions ?? null, !canAdministerOpms);
    // const consentColumns = useConsentTableColumns(institutions ?? null, !canAdministerOpms, isBct);
    const randomisedColumns = useRandomisedTableColumns(institutions ?? null);
    // const ineligibleColumns = useIneligiblePatientColumns(institutions ?? null, !canAdministerOpms, showInstitutions);

    const [screeningDialogOpen, setScreeningDialogOpen] = useState(false);
    const [patientStudyNumber, setPatientStudyNumber] = useState<string|null>(null);
    const [screeningOutcome, setScreeningOutcome] = useState<LookupScreeningOutcomeType|null>(null);

    const [consentDialogState, setConsentDialogState] = useState(ConsentDialogState.None);

    const screeningDialogContext = useMemo(() => {
        return {
            dialogOpen: screeningDialogOpen,
            setDialogOpen: setScreeningDialogOpen,
            patientStudyNumber,
            setPatientStudyNumber,
            institutionCode: parsedInstitutionCode,
            screeningOutcome,
            setScreeningOutcome
        }
    }, [screeningDialogOpen, setScreeningDialogOpen, patientStudyNumber, setPatientStudyNumber, parsedInstitutionCode, screeningOutcome, setScreeningOutcome]);

    
    const consentDialogContext = useMemo(() => {
        return {
            dialogState: consentDialogState,
            setDialogState: setConsentDialogState,
            patientStudyNumber,
            setPatientStudyNumber,
            institutionCode: parsedInstitutionCode
        }
    }, [consentDialogState, setConsentDialogState, patientStudyNumber, setPatientStudyNumber, parsedInstitutionCode]);

    //@ts-expect-error
    const onSelect = useCallback((newMasterGroupCode?: string | null, newCollaboratingGroupCode?: string | null, newCountryCode?: string | null, newInstitutionCode?: string | null) => {
        if (newInstitutionCode) {
            navigate(`/registration/${newInstitutionCode}`);
        }
        else {
            navigate(`/registration`);
        }

        return;
    }, [navigate]);

    const onRowClick: GridEventListener<'rowClick'> = useCallback((params) => {
        if (isRadiologist) {
            navigate(`/registration/${params.row?.institutionCode}/${params.row?.studyNumber}/radiologist-review/1/radiology-report/1`)
        } else {
            navigate(`/registration/${institutionCode ?? ALL_INSTITUTIONS_CODE}/${params.row?.studyNumber}`)
        }
    }, [history, institutionCode, canAdministerRadiology]);

    const onScreeningRowClick: GridEventListener<'rowClick'> = useCallback((params) => {
        setPatientStudyNumber(params.row?.studyNumber ?? null);
        setScreeningOutcome(null);
        setScreeningDialogOpen(true);
    }, [setPatientStudyNumber, setScreeningDialogOpen, setScreeningOutcome]);

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<InstitutionBreadcrumbs />}
            heading="Participants"
        >
            <PaperContainer>
                <PatientsByCodesProvider
                    
                    institutionCode={parsedInstitutionCode}
                    patientStateIds={allStates}
                >
                    <TrialContextSelector
                        onChange={onSelect}
                        allowAllMasterGroups={true}
                        allowAllCollaboratingGroups={true}
                        allowAllCountries={true}
                        allowAllInstitutions={true}
                        hideMasterGroups={true}
                        hideCollaboratingGroups={true}
                        hideCountries={true}
                        masterGroupCode={masterGroupCode}
                        collaboratingGroupCode={collaboratingGroupCode}
                        countryCode={countryCode}
                        institutionCode={institutionCode}
                        institutions={institutionsToUse}
                    >
                        {
                            <Grid2
                                size={{xs: 12, sm: 6}}
                            >
                                <Search />
                            </Grid2>
                        }  
                        <Grid2
                            size={{xs: 12, sm: 6}}
                            style={{ padding: 0}}
                        >
                        </Grid2>
                        <Grid2
                            size={{ xs: 12, sm: 6 }}
                            style={{ display: 'flex', justifyContent: 'end', paddingTop: 0 , gap: 10}}
                        >
                            {
                                !!canAdministerOpms && <FinanceMilestonesImportButton />
                            }
                            {
                                !!canPatientUpdate && <AddButton
                                    variant="contained"
                                    color="primary"
                                    //component={(Link)}
                                    //to={`/registration/${parsedInstitutionCode}/add-participant`}
                                    fullWidth
                                    disabled={!institution || (institution as Institution).recruitmentDisabled}
                                    onClick={() => setScreeningDialogOpen(true)}
                                >
                                    Add Participant
                                </AddButton>
                            }
                        </Grid2>
                        <Grid2 size={{ xs: 12 }} sx={{ display: "flex", justifyContent: "end" }}>
                            
                        </Grid2>
                    </TrialContextSelector>

                    {
                        <ScreeningDialogContext.Provider value={screeningDialogContext}>
                            <ConsentDialogContext.Provider value={consentDialogContext}>
                                <ScreeningTable />
                                <ScreeningForm refreshScreeningForms={refreshScreeningForms} />
                            </ConsentDialogContext.Provider>
                        </ScreeningDialogContext.Provider>
                    }

                    {
                        <ConsentDialogContext.Provider value={consentDialogContext}>
                            <ConsentTable />
                            <ConsentDialog />
                        </ConsentDialogContext.Provider>
                    }

                    {
                        <PatientTable
                            title={"Randomised"}
                            entityName="Participant"
                            onRowClick={onRowClick}
                            columns={randomisedColumns}
                            patientStates={randomisedStates}
                        />
                    }

                    {
                        <ScreeningDialogContext.Provider value={screeningDialogContext}>
                            <PatientTable
                                title="Failed Screenings"
                                entityName="Participant"
                                onRowClick={onScreeningRowClick}
                                columns={failedScreeningColumns}
                                patientStates={screeningFailedStates}
                                startCollapsed={true}
                            />
                        </ScreeningDialogContext.Provider>
                    }

                    {/* {
                        <PatientTable
                            title="Ineligible Participants"
                            entityName="Participant"
                            onRowClick={onRowClick}
                            columns={ineligibleColumns}
                            patientStates={ineligibleStates}
                            startCollapsed={true}
                        />
                    } */}

                    {/* <CrfFormByCodesProvider
                        createForm
                        createEvent
                        patientStudyNumber={saPatient?.studyNumber}
                        eventDefinitionIdentifier={"consent"}
                        eventRepeat={1}
                        formDefinitionIdentifier={"services-australia-consent"}
                        formRepeat={1}
                        institutionCode={saPatient?.institutionCode}
                        loadBeforeMount
                    >
                        <SaConsentDialog
                            patient={saPatient}
                            onCancel={handleCancelClick}
                            onReview={handleReviewClick}
                            open={!!saPatient}
                        />

                        <TissueConsentDialog 
                            patient={tissuePatient}
                            open={!!tissuePatient}
                        />
                    </CrfFormByCodesProvider> */}
                </PatientsByCodesProvider>
            </PaperContainer>
        </PageLayout>
    );
}



/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default Registration;
